'use client';

import type { PortableTextComponents } from '@portabletext/react';
import { PortableText } from '@portabletext/react';
import { cn } from '../../lib';
import { Container } from '../container';
import { ModuleWrapper } from '../module-wrapper';
import type { TwoColumnLayoutValue } from './portable-text-types';

// Note: TwoColumnLayoutValue is extracted from the GROQ query results
// to ensure type safety and consistency with the actual data structure

export interface TwoColumnLayoutProps {
  value: TwoColumnLayoutValue;
  components?: PortableTextComponents;
}

// Re-export the type from where it's extracted from query results
export type { TwoColumnLayoutValue } from './portable-text-types';

export function TwoColumnLayout({ value, components }: TwoColumnLayoutProps) {
  const {
    leftColumn,
    rightColumn,
    columnRatio: rawColumnRatio,
    mobileOrder: rawMobileOrder,
    moduleSettings,
    colorTheme,
  } = value;

  // Handle null values with defaults
  const columnRatio = rawColumnRatio || '1:1';
  const mobileOrder = rawMobileOrder || 'leftFirst';

  const gridClasses = {
    '1:1': 'grid-cols-1 md:grid-cols-2',
    '3:2': 'grid-cols-1 md:grid-cols-5',
    '2:3': 'grid-cols-1 md:grid-cols-5',
  };

  const colSpanClasses = {
    '1:1': ['', ''],
    '3:2': ['md:col-span-3', 'md:col-span-2'],
    '2:3': ['md:col-span-2', 'md:col-span-3'],
  };

  const mobileOrderClass =
    mobileOrder === 'rightFirst' ? 'flex-col-reverse md:flex-row' : '';

  const content = (
    <Container>
      <div
        className={cn(
          'grid gap-8 md:gap-12',
          gridClasses[columnRatio],
          mobileOrderClass
        )}
      >
        <div className={colSpanClasses[columnRatio][0]}>
          {leftColumn && (
            <PortableText value={leftColumn} components={components} />
          )}
        </div>
        <div className={colSpanClasses[columnRatio][1]}>
          {rightColumn && (
            <PortableText value={rightColumn} components={components} />
          )}
        </div>
      </div>
    </Container>
  );

  if (moduleSettings || colorTheme) {
    return (
      <ModuleWrapper
        topPadding={moduleSettings?.topPadding ?? undefined}
        bottomPadding={moduleSettings?.bottomPadding ?? undefined}
        colorTheme={colorTheme}
      >
        {content}
      </ModuleWrapper>
    );
  }

  return content;
}
